/**
 * MentionApproveForm Component
 *
 * Inline form for approving a mention with optional admin notes
 * Shared component used by both MentionReviewDetailPage and BusinessMentionCard
 */
import React from 'react';
import { Box, Stack, Text, Textarea, Group, Button } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

export interface MentionApproveFormProps {
  /**
   * Current admin notes value
   */
  adminNotes: string;

  /**
   * Callback when admin notes change
   */
  onAdminNotesChange: (notes: string) => void;

  /**
   * Callback when approve is confirmed
   */
  onConfirm: () => void;

  /**
   * Callback when cancel is clicked
   */
  onCancel: () => void;

  /**
   * Whether the form is disabled (mutation in progress)
   */
  disabled?: boolean;

  /**
   * Whether mutation is pending (shows loading state)
   */
  isPending?: boolean;
}

/**
 * MentionApproveForm - Reusable approval form
 */
export const MentionApproveForm: React.FC<MentionApproveFormProps> = ({
  adminNotes,
  onAdminNotesChange,
  onConfirm,
  onCancel,
  disabled = false,
  isPending = false
}): React.ReactElement => {
  return (
    <Box bg="green.0" p="md" style={{ borderRadius: '8px' }}>
      <Stack gap="md">
        <Text size="sm" fw={500}>
          Confirm Approval
        </Text>
        <Textarea
          label="Admin Notes (Optional)"
          placeholder="Add any notes about this approval..."
          value={adminNotes}
          onChange={(e): void => onAdminNotesChange(e.currentTarget.value)}
          rows={3}
          disabled={disabled || isPending}
        />
        <Group gap="sm">
          <Button
            color="green"
            onClick={onConfirm}
            loading={isPending}
            disabled={disabled}
            leftSection={<IconCheck size={16} />}
          >
            Confirm Approval
          </Button>
          <Button variant="subtle" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};
